import React, { useEffect, useState } from 'react';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { getStoryChapterTask } from '@/services/story-chapter-task.service';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { getMainPart, getSpecificPart, StoryChapterTask, SpecificType} from '@/models/story-chapter-task.model';
import MainStoryChapterTaskForm from './story-chapter-task-main-form';
import PageHeader from '@/components/PageHeader';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect} >
      <Nav.Item eventKey="story-chapter-task-detail">Task Detail</Nav.Item>
    </Nav>
  );
};

const Page = () => {
  
  const requestedTab = useLocation()?.state?.tab;
  const chapterId = useLocation()?.state?.chapterId;

  const [activeTab, setActiveTab] = useState('story-chapter-task-detail');
  const [formObject, setFormObject] = useState<StoryChapterTask | undefined>(undefined);
  const [specificFormObject, setSpecificFormObject] = useState<SpecificType | undefined>(undefined);
  const [loading, setLoading] = useState(0);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;
  
  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(pre => ++pre);
      try {
        const result: BaseResponseModel<StoryChapterTask & SpecificType> = await getStoryChapterTask(id);
        if (result.success) {
          setFormObject(getMainPart(result.result));
          setSpecificFormObject(getSpecificPart(result.result));
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching story chapter task: ${errorModel?.errorMessage}`);
        nav('/stories');
      }
      finally {
        setLoading(pre => --pre);
      }
    }
  };

  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit ? `Edit Chapter Task: ${formObject?.name}` : 'New Chapter Task'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/stories'> Stories</Breadcrumb.Item>
            <Breadcrumb.Item href='#' > Chapters</Breadcrumb.Item>
            <Breadcrumb.Item href='#' onClick={ () => 
              chapterId || formObject?.chapterId ? nav(`/stories/chapters/${chapterId || formObject?.chapterId}`, {state:{tab: 'story-chapter-tasks'}}) : nav(`stories/chapters`)
            }> Tasks</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={pageState == PageState.New} 
      />
      {
        !!loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'story-chapter-task-detail' && <MainStoryChapterTaskForm 
            formObject={formObject} 
            specificFormObject={specificFormObject} 
            pageState={pageState}
            chapterId={chapterId}
          />}
        </>
      }    
    </Panel>
  );
};

export default Page;
